import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  HiArrowRight, 
  HiBookOpen, 
  HiShieldCheck, 
  HiUserGroup,
  HiGlobeAlt,
  HiHeart,
  HiLightBulb
} from 'react-icons/hi2';
import { useApp } from '../context/AppContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';

const Welcome = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { preferences } = useApp();
  const isDark = preferences.darkMode || false;
  const isRTL = i18n.language === 'ar';

  const features = [
    { icon: HiBookOpen, key: 'guide' },
    { icon: HiShieldCheck, key: 'rights' },
    { icon: HiUserGroup, key: 'support' },
    { icon: HiGlobeAlt, key: 'languages' },
    { icon: HiHeart, key: 'free' },
    { icon: HiLightBulb, key: 'practical' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/30 to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header with language and dark mode */}
      <div className="fixed top-0 right-0 z-50 p-4 flex items-center gap-2">
        <DarkModeToggle />
        <LanguageSwitcher />
      </div>

      <div className="container mx-auto px-4 py-12 md:py-20">
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          {/* Logo */}
          <div className="flex justify-center mb-8 animate-fade-in">
            <img 
              src={isDark ? '/FPRA dark mode.png' : '/FPRA white mode.png'}
              alt="FPRA Logo" 
              className="h-24 md:h-32 w-auto object-contain"
            />
          </div>

          {/* Main Title */}
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-slide-up-welcome">
            {t('welcome.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-4 animate-slide-up-welcome animation-delay-200">
            {t('welcome.subtitle')}
          </p>

          {/* Description */}
          <p className="text-lg text-gray-500 dark:text-gray-400 mb-12 max-w-2xl mx-auto animate-slide-up-welcome animation-delay-400">
            {t('welcome.description')}
          </p>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/home')}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 animate-slide-up-welcome animation-delay-600"
          >
            {t('welcome.enterButton')}
            <HiArrowRight className={`h-6 w-6 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
          </button>

          {/* Secondary info */}
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400 animate-slide-up-welcome animation-delay-800">
            {t('welcome.noAccount')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('welcome.featuresTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.key}
                  className="group bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-3 bg-gradient-to-br from-teal-100 to-teal-50 dark:from-teal-900 dark:to-teal-800 rounded-xl group-hover:scale-110 transition-transform">
                      <Icon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {t(`welcome.features.${feature.key}.title`)}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {t(`welcome.features.${feature.key}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Section */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('welcome.mission.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 italic">
              "{t('welcome.mission.description')}"
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>—</span>
              <span className="font-semibold">FPRA</span>
              <span>•</span>
              <span>{t('welcome.mission.subtitle')}</span>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/home')}
            className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium inline-flex items-center gap-2 group"
          >
            {t('welcome.startNow')}
            <HiArrowRight className={`h-5 w-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Custom animations - moved to index.css */}
    </div>
  );
};

export default Welcome;
